import { useState } from 'react';
import Sheet from './Sheet';
import Icon from './Icon';
import { PRIMARY } from './CaptureSheets';
import { shortDate, toISODate } from '../lib/dates';
import { useApp } from '../lib/AppContext';
import { useTrialOfferShown } from './Trial';
import { useFeedback } from './FeedbackChat';

export function ProBadge({ className = '' }) {
  return (
    <span className={`rounded-full bg-[#f4c026] px-1.5 py-px text-[10px] font-extrabold leading-tight text-[#17201b] ${className}`}>PRO</span>
  );
}

// Prices shown until App Store purchases are wired up (RevenueCat); the yearly plan is the default choice. Two
// plans keep the choice simple; set to cover Apple's 15% and Vietnamese taxes with a margin over AI costs
// (docs/ke-hoach-sau-khi-co-tai-khoan-apple.md, 4.1). First-time buyers see the introductory price: 29.000 ₫ for
// the first month, 449.000 ₫ for the first year (an App Store introductory offer: once per Apple ID).
const PLANS = [
  { id: 'yearly', label: 'Theo năm', price: '549.000 ₫/năm', note: '≈ 45.750 ₫/tháng · tiết kiệm 42%',
    intro: { price: '449.000 ₫', note: 'Năm đầu tiên, sau đó 549.000 ₫/năm' } },
  { id: 'monthly', label: 'Theo tháng', price: '79.000 ₫/tháng', note: null,
    intro: { price: '29.000 ₫', note: 'Tháng đầu tiên, sau đó 79.000 ₫/tháng' } },
];

// "Sau đó, Pro từ …" in the trial terms: the cheapest per month (the yearly plan). Keep in step with PLANS; once
// RevenueCat is wired, both come from StoreKit in the user's currency.
const PRO_FROM = '45.750 ₫/tháng (gói năm 549.000 ₫)';
const PRO_FROM_MONTHLY = '45.750 ₫/tháng';

// On the website, until payments go through the App Store, "Đăng ký Pro" opens the chat with the team ("Mua Pro")
// with this message written for them, to edit before sending; the team replies with how to pay and gives Pro by
// hand. Never in the iOS/Android app: buying outside In-App Purchase isn't allowed there (Guideline 3.1.1, 3.1.3).
const buyMessage = (plan, intro) => {
  const price = intro ? `${intro.price} (${intro.note.toLowerCase()})` : plan.price;
  return `Chào Macro Mate, mình muốn mua Pro gói ${plan.label.toLowerCase()}: ${price}. Bạn hướng dẫn mình cách thanh toán nhé.`;
};

const BENEFITS = [
  'Thực đơn tuần do AI lên theo mục tiêu của bạn, kèm danh sách đi chợ',
  'Phân tích ảnh và mô tả bằng AI thoải mái hơn (20 lượt mỗi ngày, 200 lượt mỗi tháng)',
  'Không quảng cáo',
];

// What Apple asks to be said before a free trial starts (App Review Guideline 3.1.1): how long it lasts, what stops
// when it ends, and what keeping Pro costs. Ours needs no payment and can't renew or charge by itself.
export function TrialTerms({ membership }) {
  const { native } = useApp();
  const days = membership?.trial_length_days || 7;
  // It ends `days` days after the tap, at the same time of day (Membership::TRIAL_LENGTH).
  const end = new Date(Date.now() + days * 86400000);
  const ends = `${end.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} ngày ${shortDate(toISODate(end))}`;
  const free = membership?.ads?.enabled
    ? `mỗi lượt AI cần xem một quảng cáo (tối đa ${membership.ads.daily} lượt mỗi ngày)`
    : `${membership?.credits?.daily ?? 3} lượt AI mỗi ngày`;
  const terms = [
    `Miễn phí ${days} ngày, đến ${ends}.`,
    'Không cần thanh toán, không tự gia hạn, không trừ tiền khi hết hạn.',
    `Hết ${days} ngày, tài khoản tự về bản Free: không còn thực đơn tuần và đổi món bằng AI, ${free}, có quảng cáo. Dữ liệu vẫn giữ nguyên.`,
    `Muốn dùng tiếp: Pro từ ${PRO_FROM}, ${native ? 'đăng ký qua App Store' : 'nhắn đội ngũ ngay trong app để đăng ký'}.`,
  ];
  return (
    <ul className="flex flex-col gap-1.5 rounded-[16px] bg-base-200 p-4 text-sm">
      {terms.map((t) => <li key={t} className="flex gap-2"><span aria-hidden="true" className="text-muted">•</span><span>{t}</span></li>)}
    </ul>
  );
}

const HEADLINES = {
  trial_offer: { title: 'Dùng thử Pro miễn phí 7 ngày', text: 'Trải nghiệm đầy đủ Macro Mate, không cần thanh toán.' },
  trial_ended: { title: 'Macro Mate Pro', text: 'Đăng ký Pro để dùng tiếp thực đơn tuần, đổi món và nhiều lượt AI hơn.' },
  pro_required: { title: 'Tính năng dành cho Pro', text: 'Thực đơn tuần và đổi món bằng AI có trong gói Pro.' },
  credits: { title: 'Bạn đã dùng hết lượt AI hôm nay', text: 'Lượt AI miễn phí làm mới lúc 0 giờ. Nâng cấp để dùng AI không giới hạn.' },
  upgrade: { title: 'Macro Mate Pro', text: 'Mở khóa toàn bộ tính năng AI.' },
};

const FIRST_AI_BENEFITS = [
  { icon: 'camera', title: '20 lượt AI mỗi ngày', text: 'Chụp mọi bữa, không phải đếm lượt' },
  { icon: 'calendar', title: 'Thực đơn tuần theo mục tiêu', text: 'Kèm danh sách đi chợ' },
  { icon: 'sparkles', title: 'Không quảng cáo', text: 'Không cần xem quảng cáo để có thêm lượt' },
];

// Shown once, after the first AI meal is saved: tied to what was just done, the terms right under the button.
function FirstAiOffer({ offer, busy, error, onStart, onLater }) {
  const { membership } = useApp();
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand-text">
          <Icon name="check" className="size-4" /> Bữa đầu tiên đã ghi{offer?.calories != null ? ` · ${offer.calories} kcal` : ''}
        </p>
        <p className="mt-1 text-lg font-bold">Muốn AI lo hết các bữa? Dùng thử Pro 7 ngày</p>
      </div>
      <ul className="flex flex-col gap-3">
        {FIRST_AI_BENEFITS.map((b) => (
          <li key={b.title} className="flex items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[#e3f1e9] text-[#17804f]"><Icon name={b.icon} className="size-5" /></span>
            <span><span className="block text-[15px] font-semibold">{b.title}</span><span className="block text-sm text-muted">{b.text}</span></span>
          </li>
        ))}
      </ul>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <button type="button" className={PRIMARY} disabled={busy} onClick={onStart}>
        {busy && <span className="loading loading-spinner loading-sm" />} Dùng thử miễn phí 7 ngày
      </button>
      <TrialTerms membership={membership} />
      <button type="button" className="btn h-12 rounded-2xl border-line bg-base-100 font-semibold" disabled={busy} onClick={onLater}>Để sau</button>
    </div>
  );
}

// "Tuần qua bạn đã làm được", once the trial is over: what the 7 days brought, then Pro or carry on with Free.
function TrialSummary({ summary, onPlans, onClose }) {
  const stats = [
    { value: summary.ai_used, label: 'lượt AI đã dùng' },
    { value: summary.meals, label: 'bữa đã ghi' },
    { value: `${summary.protein_days}/${summary.days}`, label: 'ngày đạt đạm' },
  ];
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-muted">Hết 7 ngày dùng thử Pro</p>
        <p className="mt-1 text-lg font-bold">Tuần qua bạn đã làm được</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="rounded-[14px] bg-base-200 p-3">
            <div className="tabular text-2xl font-extrabold">{s.value}</div>
            <div className="text-xs text-muted">{s.label}</div>
          </div>
        ))}
      </div>
      <p className="text-sm text-muted">
        Từ hôm nay bạn về bản Free: không còn thực đơn tuần và đổi món bằng AI, ít lượt AI hơn mỗi ngày. Nhật ký và thực đơn đã tạo vẫn giữ nguyên.
      </p>
      <button type="button" className={PRIMARY} onClick={onPlans}>Đăng ký Pro · từ {PRO_FROM_MONTHLY}</button>
      <button type="button" className="btn h-12 rounded-2xl border-line bg-base-100 font-semibold" onClick={onClose}>Tiếp tục với Miễn phí</button>
    </div>
  );
}

function ProContent({ reason, placement, onClose }) {
  const { api, membership, setMembership, native } = useApp();
  const { openFeedback } = useFeedback() || {};
  const [note, setNote] = useState(null);
  const [plan, setPlan] = useState('yearly');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);
  const [plans, setPlans] = useState(false);
  const headline = HEADLINES[reason] || HEADLINES.upgrade;
  const canTrial = membership?.trial_available;
  // The first-AI offer keeps its meal: membership drops it once the offer has been recorded as shown.
  const [firstAi] = useState(membership?.first_ai_offer);
  // Every view but the trial summary shows the trial while it's still available.
  const where = reason === 'first_ai' ? 'first_ai' : placement || 'paywall';
  useTrialOfferShown(where, canTrial && reason !== 'trial_ended');

  const buy = () => {
    if (native) {
      setNote('Thanh toán qua App Store sẽ có khi Macro Mate phát hành trên App Store.');
      return;
    }
    const chosen = PLANS.find((p) => p.id === plan);
    onClose({ buying: true });
    openFeedback?.({ mode: 'personal', category: 'buy_pro', body: buyMessage(chosen, membership?.intro_offer ? chosen.intro : null) });
  };

  const startTrial = async () => {
    setBusy(true);
    setError(null);
    try {
      setMembership(await api.post('/membership/trial', { placement: where }));
      setStarted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (started) {
    const ends = membership?.trial_ends_at ? shortDate(toISODate(new Date(membership.trial_ends_at))) : null;
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <span className="grid size-16 place-items-center rounded-full bg-[#e3f1e9] text-[#17804f]"><Icon name="check" className="size-8" /></span>
        <div>
          <p className="text-lg font-bold">Bạn đang dùng thử Pro</p>
          <p className="text-sm text-muted">
            {ends ? `Đến hết ngày ${ends}. ` : ''}Không cần thanh toán: sau đó tài khoản tự về bản thường, dữ liệu vẫn giữ nguyên.
          </p>
        </div>
        <button type="button" className={PRIMARY} onClick={onClose}>Bắt đầu</button>
      </div>
    );
  }

  if (reason === 'first_ai' && canTrial) {
    return <FirstAiOffer offer={firstAi} busy={busy} error={error} onStart={startTrial} onLater={onClose} />;
  }
  if (reason === 'trial_ended' && membership?.trial_summary && !plans) {
    return <TrialSummary summary={membership.trial_summary} onPlans={() => setPlans(true)} onClose={onClose} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2"><ProBadge /><span className="text-lg font-bold">{headline.title}</span></div>
        <p className="text-sm text-muted">{headline.text}</p>
      </div>

      <ul className="flex flex-col gap-2.5">
        {BENEFITS.map((b) => (
          <li key={b} className="flex gap-2.5 text-[15px]">
            <Icon name="check" className="mt-0.5 size-5 shrink-0 text-brand-text" /> {b}
          </li>
        ))}
      </ul>

      {canTrial && (
        <div className="flex flex-col gap-3">
          <TrialTerms membership={membership} />
          <button type="button" className={PRIMARY} disabled={busy} onClick={startTrial}>
            {busy && <span className="loading loading-spinner loading-sm" />} Bắt đầu 7 ngày miễn phí
          </button>
        </div>
      )}

      <div role="radiogroup" aria-label="Gói Pro" className="flex flex-col gap-2">
        {PLANS.map((p) => {
          const intro = membership?.intro_offer ? p.intro : null;
          return (
            <button key={p.id} type="button" role="radio" aria-checked={plan === p.id} onClick={() => setPlan(p.id)}
              className={`flex min-h-14 items-center justify-between gap-3 rounded-[16px] border-2 px-4 py-3 text-left
                ${plan === p.id ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
              <span>
                <span className="block font-semibold">{p.label}</span>
                <span className="block text-xs text-brand-text">{intro ? intro.note : p.note}</span>
              </span>
              <span className="tabular shrink-0 whitespace-nowrap text-right">
                {intro && <span className="block text-xs text-muted line-through">{p.price}</span>}
                <span className="block font-bold">{intro ? intro.price : p.price}</span>
              </span>
            </button>
          );
        })}
      </div>
      {/* Paying needs the App Store build (In-App Purchase); until then, on the website only, it messages the team. */}
      <div className="flex flex-col gap-1.5">
        <button type="button" className={canTrial ? 'btn h-12 rounded-2xl border-primary bg-base-100 font-bold text-brand-text' : PRIMARY}
          onClick={buy}>
          Đăng ký Pro
        </button>
        {native
          ? note && <p role="status" className="text-center text-sm text-muted">{note}</p>
          : <p className="text-center text-xs text-muted">Nhắn đội ngũ Macro Mate, mình gửi cách thanh toán và kích hoạt Pro cho bạn.</p>}
      </div>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}

      <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={onClose}>Để sau</button>
    </div>
  );
}

// Opened from anywhere through useApp().openPaywall(reason, { placement }): placement says where a trial offer
// was shown (onboarding | first_ai | quota | paywall).
export default function ProSheet({ reason, placement, onClose }) {
  return (
    <Sheet open={!!reason} onClose={onClose} title="Macro Mate Pro">
      {reason && <ProContent reason={reason} placement={placement} onClose={onClose} />}
    </Sheet>
  );
}
